<template>
  <div>
    <slot
      :data="data"
      :confidence-summary="confidenceSummary"
      :current-dashboard-user="currentDashboardUser"
      :is-first-load="isFirstLoad"
      :is-loading="isLoading"
      :insights-available="insightsAvailable"
      :monthly-total="monthlyTotal"
      :current-no-filters-total="currentNoFiltersTotal"
      :pagination="pagination"
      :period="period"
      :previous-60-days-total-duration="previous60DaysTotalDuration"
      :set-page="setPage"
      :total-duration="totalDuration"
      :total-instances="totalInstances"
      :total-members="totalMembers"
      :total-instances-by-confidence-levels="totalInstancesByConfidenceLevels"
      :sort="sort"
      :sort-options="sortOptions"
      :filter-stats="filterStats"
      :has-data="hasData"
    ></slot>
  </div>
</template>

<script>
/**
 * Data-fetching container for Insights Unusual Activity Widgets
 */
import { INSIGHTS } from '~/app_constants';
import { Insights, InsightsFilters, InsightsDateRanges } from '~/mixins';
import { UnusualActivitiesResource } from '~/resources';
import { mapActions, mapGetters, mapState } from 'vuex';
import { pluralize } from '~/utils/string_utils';

import { get, snakeCase, sortBy } from 'lodash-es';

export default {
  name: 'InsightsUnusualActivityWidgetContainer',
  mixins: [Insights, InsightsFilters, InsightsDateRanges],
  props: {
    timeZone: {
      type: String,
      required: true,
    },
    expanded: {
      type: Boolean,
      default: true,
    },
    limit: {
      type: Number,
      default: 0,
    },
    widgetKey: {
      type: String,
      required: true,
    },
  },
  data: () => ({
    showDialog: {
      team: false,
      member: false,
    },
    prevParams: {}, // keep sort and other options
  }),
  computed: {
    ...mapState({
      organizationInsightsUrl: 'organization_insights_url',
    }),
    ...mapGetters([
      'insightsAvailable',
      'hasNoInsightsUaPreview',
      'isUserDashboard',
      'currentDashboardUser',
      'filters',
    ]),
    forcePreview() {
      return this.hasNoInsightsUaPreview;
    },
    widgetData() {
      return get(this.$store.state.widgets, this.widgetKey) || {};
    },
    isLoading() {
      // Only `false` turns it off; everything else (undefined, null, true, 0, etc.) keeps loading.
      return this.loadingWidgets?.[this.widgetKey] !== false;
    },
    sortOptions() {
      return this.widgetData?.sort || {};
    },
    hasTableData() {
      const key = this.isUserDashboard ? 'unusual_activities' : 'users';

      return this.data?.[key] && Object.keys(this.data?.[key] ?? {}).length > 0;
    },
    totalMembers() {
      return this.totalStats.members;
    },
    totalInstances() {
      return this.totalStats.instances;
    },
    totalDuration() {
      return this.totalStats.total_duration;
    },
    hasData() {
      return !!(this.totalInstances || this.totalMembers || this.totalDuration);
    },
    totalInstancesByConfidenceLevels() {
      return this.totalStats.instances_by_confidence_levels;
    },
    previous60DaysTotalDuration() {
      return this.totalStats.previous_60_days_total_duration;
    },
    totalStats() {
      return this.widgetData.data?.total_stats || {};
    },
    confidenceSummary() {
      if (Object.keys(this.widgetData.data?.total_stats ?? {}).length) {
        return {
          ...this.widgetData.data?.total_stats,
          reasons: this.widgetData.data?.dates_by_confidence_levels,
        };
      }

      return null;
    },
    pagination() {
      return this.widgetData.pagination || {};
    },
    monthlyTotal() {
      const stats = this.widgetData.data?.stats;

      if (!stats) {
        return 0;
      }

      return Object.values(stats).reduce((total, value) => total + parseInt(value || 0, 10), 0);
    },
    currentNoFiltersTotal() {
      const stats = this.filterStats.confidence_levels;

      if (!stats) {
        return 0;
      }

      return Object.values(stats).reduce((total, value) => total + parseInt(value || 0, 10), 0);
    },
    data() {
      if (this.isLoading || this.isFirstLoad) {
        return {};
      }
      const stats = this.widgetData.data?.stats;

      if (this.expanded) {
        return this.widgetData?.data;
      }
      if (this.period === 'requested') {
        return this.widgetData.data;
      }
      if (stats) {
        return {
          ...this.widgetData.data,
          stats: {},
        };
      }

      return {};
    },
    chartData() {
      if (this.isLoading) {
        return [];
      }
      const data = Object.entries(INSIGHTS.unusualActivity.labels).map(([key, label]) => {
        // Since the BE is returning number of instances, and we are looking for percentages in the charts
        // we need to calculate it with a simple rule of three: multiplying the instances by 100 and dividing by the total number.
        // Our aim is to get a percentage with one decimal, so we multiply and then divide by 10
        const value = this.totalInstances
          ? Math.round(((this.chart[snakeCase(key)] * 100) / this.totalInstances) * 10) / 10
          : 0;

        return {
          key,
          label: label.text,
          value,
          tooltip: this.formatTooltip(snakeCase(key), label.text),
        };
      });

      return sortBy(data, 'value');
    },
    userId() {
      return this.currentDashboardUser?.id;
    },
    chart() {
      return (this.isRequestedDataPresent && this.data?.stats) || {};
    },
    users() {
      return this.data?.users || [];
    },
    period() {
      return this.widgetData?.data?.period;
    },
    isRequestedDataPresent() {
      return this.period === 'requested' || !this.period;
    },
    filterStats() {
      return this.widgetData.data?.filter_stats || {};
    },
    hasActualUserData() {
      const users = this.widgetData?.data?.users;
      const ua = this.widgetData?.data?.unusual_activities;

      return (Array.isArray(users) && users.length > 0) || (Array.isArray(ua) ? ua.length > 0 : Boolean(ua));
    },
    shouldMarkAsReviewed() {
      return (
        !this.forcePreview &&
        this.userId &&
        !this.isLoading &&
        !this.isFirstLoad &&
        this.hasActualUserData
      );
    },
  },
  watch: {
    shouldMarkAsReviewed(newValue) {
      if (newValue && this.userId) {
        this.markUserActivityReviewed(this.userId);
      }
    },
    'filters.date'(newVal, oldVal) {
      if (this.forcePreview) {
        return;
      }
      if (newVal && oldVal && (newVal.from !== oldVal.from || newVal.to !== oldVal.to)) {
        this.loadData({
          page: 1,
          from: newVal.from,
          to: newVal.to,
        });
      }
    },
  },
  async created() {
    // Non-Insights preview mode owns its own (backend-driven) date window and ignores the
    // dashboard's date filter - so we only forward the range in the normal path. Sort is local
    // to this widget (see sort()) and resets to the backend default on each mount.
    const params = {
      ...(this.limit ? { limit: this.limit } : {}),
      preview: this.forcePreview || !this.expanded,
      time_zone: this.timeZone,
    };

    if (!this.forcePreview) {
      if (this.filters.date?.from) {
        params.from = this.filters.date.from;
      }
      if (this.filters.date?.to) {
        params.to = this.filters.date.to;
      }
    }
    this.prevParams = {};
    await this.loadData(params);
    this.isFirstLoad = false;
    this.trackPageViewed();
  },
  methods: {
    ...mapActions(['fetchWidgets']),
    toggleDialog() {
      const dialog = this.isUserDashboard ? 'member' : 'team';

      this.showDialog[dialog] = !this.showDialog[dialog];
    },
    sort(options) {
      const params = {
        sort_by: options.field,
        sort_order: options.direction,
        time_zone: this.timeZone,
        page: options.page || 1,
        ...(this.filters?.confidence_levels
          ? { filters: { confidence_levels: this.filters.confidence_levels } }
          : {}),
      };

      // Sort is widget-local and accumulates in prevParams for the lifetime of this mount.
      this.loadData(params);
    },
    setPage(page) {
      window.analytics?.track('Unusual pagination selected');
      this.loadData({ page });
    },
    async loadData(params) {
      if (!this.isReady) {
        return;
      }
      await this.$nextTick();
      this.prevParams = { ...this.prevParams, ...params };
      await this.fetchWidgets({
        widgetKey: this.widgetKey,
        extraQueryParams: this.prevParams,
      });
    },
    trackPageViewed() {
      const confidenceLevels = this.filterStats?.confidence_levels || {};

      try {
        window.analytics?.track('Unusual activity page viewed', {
          addons: this.insightsAvailable ? 'insights' : 'none',
          highly_unusual_instances_count: confidenceLevels.high || 0,
          unusual_instances_count: confidenceLevels.medium || 0,
          slightly_unusual_instances_count: confidenceLevels.low || 0,
          members_flagged_count: this.totalMembers || 0,
          total_time_flagged: this.totalDuration || 0,
        });
      } catch (_error) {
        // Keep widget load resilient if analytics SDK fails.
      }
    },
    switchMember(id) {
      const string = `${id}_user_id`;

      this.setPreviousView({ previousView: this.selectedFilter, currentView: string });
      this.setSelectedFilter(string);
      this.setMembersFilter(string);
    },
    formatTooltip(key, label) {
      const instances = this.chart[key] || 0;

      if (this.isUserDashboard) {
        return `<div>${instances} ${pluralize(instances, 'instance')}</div>`;
      }

      const members = this.users.filter((user) => user?.stats?.[key]).length || 0;

      return [
        '<div class="d-flex flex-column">',
        `<div>${instances} ${pluralize(instances, 'instance')}</div>`,
        `<div>${label}</div>`,
        members ? `<div>${members} ${pluralize(members, 'member')}</div>` : '',
        '</div>',
      ].join('');
    },
    async markUserActivityReviewed(userId) {
      if (!userId) {
        return;
      }

      try {
        const params = {
          ...(this.filters.date?.from && { from: this.filters.date.from }),
          ...(this.filters.date?.to && { to: this.filters.date.to }),
        };
        const response = await UnusualActivitiesResource.markReviewed(
          { organizationId: this.$store.state.organization.id, userId },
          params,
        );

        if (response.success) {
          this.updateSidebarNotificationBadge(response.notification_count);
        } else {
          console.warn('Failed to mark unusual activity as reviewed:', response.message);
        }
      } catch (error) {
        console.warn('Error marking unusual activity as reviewed:', error);
      }
    },
    updateSidebarNotificationBadge(notificationCount) {
      try {
        const badgeElements = document.querySelectorAll('.unusual-activity-notification-badge');

        if (!badgeElements.length) {
          return;
        }

        const normalizedCount = notificationCount == null ? null : String(notificationCount);

        if (normalizedCount === '0' || normalizedCount === null) {
          const containerElement = document.getElementById('insights-notification-badge');

          if (containerElement) {
            containerElement.remove();
          }

          badgeElements.forEach((badgeElement) => {
            badgeElement.remove();
          });
        } else {
          badgeElements.forEach((badgeElement) => {
            const numberElement = badgeElement.querySelector('.number');

            if (numberElement) {
              numberElement.textContent = normalizedCount;
            }
          });
        }
      } catch (error) {
        console.warn('Error updating sidebar notification badge:', error);
      }
    },
  },
};
</script>
